import { createContextScope, type Scope } from '@radix-ui/react-context'
import {
  useVirtualizer,
  type VirtualItem,
  type Virtualizer as ReactVirtualizer,
} from '@tanstack/react-virtual'
import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react'

type ScopedProps<P> = P & { __scopeVirtualizer?: Scope }

type VirtualizerOptions = {
  count: number
  estimate?: number
  overscan?: number
}

type VirtualizerContextValue = {
  virtualizer: ReactVirtualizer<HTMLDivElement, Element>
  options: VirtualizerOptions
  loadingState: [
    React.ReactNode,
    React.Dispatch<React.SetStateAction<React.ReactNode>>,
  ]
  errorState: [
    React.ReactNode,
    React.Dispatch<React.SetStateAction<React.ReactNode>>,
  ]
  listRef: React.RefObject<HTMLDivElement>
}

type VirtualizerListOptions = {
  height?: string | number
  maxHeight?: number
  onScrollEnd?: () => Promise<unknown>
}

type VirtualizerProps = {
  options: VirtualizerOptions
  children: React.ReactNode
}

type VirtualizerScrollToOffsetProps = {
  offset: number
}

type VirtualizerLoadingProps = {
  children: React.ReactNode
}

type VirtualizerErrorProps = {
  children: React.ReactNode
}

type VirtualizerListProps = {
  options?: VirtualizerListOptions
  children: (virtualRow: VirtualItem) => React.ReactNode
}

const VIRTUALIZER_NAME = 'Virtualizer'
const SCROLL_TO_OFFSET_NAME = 'VirtualizerScrollToOffset'
const LOADING_NAME = 'VirtualizerLoading'
const ERROR_NAME = 'VirtualizerError'
const LIST_NAME = 'VirtualizerList'

const [createVirtualizerContext] = createContextScope(VIRTUALIZER_NAME)
const [VirtualizerProvider, useVirtualizerContext] =
  createVirtualizerContext<VirtualizerContextValue>(VIRTUALIZER_NAME)

const defaultOptions = {
  estimate: 36,
  overscan: 5,
}

const defaultListOptions = {
  maxHeight: 140,
}

function getHeight(
  options: VirtualizerListOptions,
  totalHeight: number,
): number | string {
  if (options.maxHeight) {
    return totalHeight > options.maxHeight ? options.maxHeight : totalHeight
  }

  if (options.height) {
    return options.height
  }

  return totalHeight > defaultListOptions.maxHeight
    ? defaultListOptions.maxHeight
    : totalHeight
}

const Virtualizer = forwardRef<
  React.ElementRef<typeof VirtualizerProvider>,
  ScopedProps<VirtualizerProps>
>(
  (
    { __scopeVirtualizer, options, children }: ScopedProps<VirtualizerProps>,
    forwardedRef,
  ) => {
    const mergedOptions = { ...defaultOptions, ...options }
    const { count, estimate, overscan } = mergedOptions
    const listRef = useRef<HTMLDivElement>(null)
    const loadingState = useState<React.ReactNode>(null)
    const errorState = useState<React.ReactNode>(null)
    const virtualizer = useVirtualizer({
      count,
      getScrollElement: () => listRef.current,
      estimateSize: () => estimate,
      overscan,
    })

    return (
      <VirtualizerProvider
        scope={__scopeVirtualizer}
        virtualizer={virtualizer}
        options={mergedOptions}
        loadingState={loadingState}
        errorState={errorState}
        listRef={listRef}
      >
        <div ref={forwardedRef}>{children}</div>
      </VirtualizerProvider>
    )
  },
)
Virtualizer.displayName = VIRTUALIZER_NAME

const VirtualizerScrollToOffset = ({
  __scopeVirtualizer,
  offset,
}: ScopedProps<VirtualizerScrollToOffsetProps>) => {
  const { virtualizer } = useVirtualizerContext(
    VIRTUALIZER_NAME,
    __scopeVirtualizer,
  )

  useLayoutEffect(() => {
    virtualizer.scrollToOffset(offset)
  }, [offset, virtualizer])

  return false
}
VirtualizerScrollToOffset.displayName = SCROLL_TO_OFFSET_NAME

const VirtualizerLoading = ({
  __scopeVirtualizer,
  children,
}: ScopedProps<VirtualizerLoadingProps>) => {
  const { loadingState } = useVirtualizerContext(
    VIRTUALIZER_NAME,
    __scopeVirtualizer,
  )
  const [, setLoading] = loadingState

  useEffect(() => {
    if (children) {
      setLoading(children)
    }

    return () => setLoading(null)
  }, [children, setLoading])

  return false
}
VirtualizerLoading.displayName = LOADING_NAME

const VirtualizerError = ({
  __scopeVirtualizer,
  children,
}: ScopedProps<VirtualizerErrorProps>) => {
  const { loadingState } = useVirtualizerContext(
    VIRTUALIZER_NAME,
    __scopeVirtualizer,
  )
  const [, setError] = loadingState

  useEffect(() => {
    if (children) {
      setError(children)
    }

    return () => setError(null)
  }, [children, setError])

  return false
}
VirtualizerError.displayName = ERROR_NAME

const VirtualizerList = ({
  __scopeVirtualizer,
  options = {},
  children,
}: ScopedProps<VirtualizerListProps>) => {
  const { onScrollEnd } = options
  const {
    virtualizer,
    options: contextOptions,
    loadingState,
    errorState,
    listRef,
  } = useVirtualizerContext(VIRTUALIZER_NAME, __scopeVirtualizer)
  const [loading] = loadingState
  const [error] = errorState
  const isScrollEndTriggered = useRef(false)

  const items = virtualizer.getVirtualItems()
  const totalHeight = virtualizer.getTotalSize()
  const height = getHeight(options, totalHeight)

  useEffect(() => {
    if (onScrollEnd) {
      const [visibleItem] = [...items].reverse()

      if (
        visibleItem?.index === contextOptions.count - 1 &&
        !isScrollEndTriggered.current
      ) {
        isScrollEndTriggered.current = true

        return () => {
          onScrollEnd().finally(() => (isScrollEndTriggered.current = false))
        }
      }
    }
  })

  return (
    <div
      className="w-full overflow-y-auto [contain:strict]"
      style={{ height }}
      ref={listRef}
    >
      <div className="relative w-full" style={{ height: totalHeight }}>
        <div
          className="absolute left-0 top-0 w-full"
          style={{
            transform: `translateY(${items[0]?.start ?? 0}px)`,
          }}
        >
          {items.map((virtualRow) => {
            return (
              <div
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                key={virtualRow.key}
              >
                {children(virtualRow)}
              </div>
            )
          })}

          {loading || false}

          {error || false}
        </div>
      </div>
    </div>
  )
}
VirtualizerList.displayName = LIST_NAME

export {
  Virtualizer,
  VirtualizerScrollToOffset,
  VirtualizerLoading,
  VirtualizerError,
  VirtualizerList,
}
